import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useWorkspace } from "@/hooks/useWorkspace";
import { composerBus } from "@/lib/composerBus";
import { templateToBriefDocument } from "@/lib/composerPushDocs";
import {
  cloneExcerptusTemplate,
  createUserTemplate,
  deleteUserTemplate,
  duplicateUserTemplate,
  fetchPublishedExcerptusTemplates,
  fetchUserTemplates,
  reorderUserTemplates,
  templateKind,
  updateUserTemplateFromDoc,
  type ExcerptusTemplate,
  type TemplateKind,
  type UserTemplate,
} from "@/lib/composerTemplates";
import type { BriefDocument } from "@/lib/briefDocument";
import type { Json } from "@/integrations/supabase/types";

export type PreviewState =
  | { kind: "excerptus"; template: ExcerptusTemplate }
  | { kind: "user"; template: UserTemplate }
  | null;

export function useTemplatesPanel(getCurrentDoc?: () => BriefDocument | null) {
  const { active } = useWorkspace();
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const [excerptus, setExcerptus] = useState<ExcerptusTemplate[]>([]);
  const [userTemplates, setUserTemplates] = useState<UserTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<UserTemplate | null>(null);
  const [tab, setTab] = useState<"excerptus" | "mine">("mine");
  const [contentTypeFilter, setContentTypeFilter] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<TemplateKind | "all">("all");
  const [preview, setPreview] = useState<PreviewState>(null);

  const isOwner = useMemo(
    () => Boolean(active && user && active.owner_user_id === user.id) || isAdmin,
    [active, user, isAdmin],
  );

  const refresh = async () => {
    if (!active) return;
    setLoading(true);
    try {
      const [ex, ut] = await Promise.all([
        fetchPublishedExcerptusTemplates(),
        fetchUserTemplates(active.id),
      ]);
      setExcerptus(ex);
      setUserTemplates(ut);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  useEffect(() => {
    setContentTypeFilter(null);
    setKindFilter("all");
  }, [tab]);

  const tabContentTypes = useMemo(() => {
    const set = new Set<string>();
    const list = tab === "excerptus" ? excerptus : userTemplates;
    for (const t of list) set.add(t.content_type);
    return Array.from(set).sort();
  }, [tab, excerptus, userTemplates]);

  const filterByQuery = <
    T extends { name: string; description: string | null; content_type: string; wizard_questions: Json | null },
  >(
    list: T[],
  ): T[] => {
    const q = search.trim().toLowerCase();
    return list.filter((t) => {
      if (contentTypeFilter && t.content_type !== contentTypeFilter) return false;
      if (kindFilter !== "all" && templateKind(t) !== kindFilter) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        (t.description ?? "").toLowerCase().includes(q) ||
        t.content_type.toLowerCase().includes(q)
      );
    });
  };

  const handleClone = async (t: ExcerptusTemplate) => {
    if (!active) return;
    try {
      const created = await cloneExcerptusTemplate(t, active.id);
      setUserTemplates((cur) => [...cur, created]);
      toast.success(`Saved "${created.name}" to My Templates`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleDelete = async (t: UserTemplate) => {
    if (!confirm(`Delete template "${t.name}"?`)) return;
    try {
      await deleteUserTemplate(t.id);
      toast.success("Template deleted");
      void refresh();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleDuplicateMine = async (t: UserTemplate) => {
    try {
      const created = await duplicateUserTemplate(t);
      setUserTemplates((cur) => [...cur, created]);
      toast.success(`Duplicated as "${created.name}"`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleUpdateFromDraft = async (t: UserTemplate) => {
    if (!getCurrentDoc) return;
    const doc = getCurrentDoc();
    if (!doc) {
      toast.error("Open a Draft Canvas first");
      return;
    }
    if (
      !confirm(
        `Replace structure of "${t.name}" with the current Draft? Wizard questions and intro message are kept.`,
      )
    )
      return;
    try {
      await updateUserTemplateFromDoc(t.id, doc);
      toast.success(`Updated "${t.name}" from current Draft`);
      void refresh();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleCreateNew = async () => {
    if (!active) return;
    try {
      const created = await createUserTemplate(active.id, {
        name: "Untitled template",
        content_type: "essay",
      });
      setUserTemplates((cur) => [...cur, created]);
      setEditing(created);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const pushTemplateToCanvas = (
    t: { name: string; intro_message?: string | null; default_structure: unknown },
  ) => {
    const doc = templateToBriefDocument({
      introMessage: t.intro_message ?? null,
      defaultStructure: t.default_structure,
    });
    composerBus.emit("draft:insert", { doc, mode: "replace-clean", source: "template", label: t.name });
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    if (!active) return;
    const { active: a, over } = e;
    if (!over || a.id === over.id) return;
    const oldIndex = userTemplates.findIndex((t) => t.id === a.id);
    const newIndex = userTemplates.findIndex((t) => t.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(userTemplates, oldIndex, newIndex);
    setUserTemplates(next);
    try {
      await reorderUserTemplates(active.id, next.map((t) => t.id));
    } catch (err) {
      toast.error((err as Error).message ?? "Could not save order");
      void refresh();
    }
  };

  const filteredExcerptus = filterByQuery(excerptus);
  const filteredUser = filterByQuery(userTemplates);
  const updateDisabledReason =
    !getCurrentDoc || !getCurrentDoc() ? "Open a Draft Canvas first" : undefined;

  return {
    isOwner,
    loading,
    search, setSearch,
    editing, setEditing,
    tab, setTab,
    contentTypeFilter, setContentTypeFilter,
    kindFilter, setKindFilter,
    preview, setPreview,
    tabContentTypes,
    filteredExcerptus,
    filteredUser,
    updateDisabledReason,
    refresh,
    handleClone,
    handleDelete,
    handleDuplicateMine,
    handleUpdateFromDraft,
    handleCreateNew,
    pushTemplateToCanvas,
    handleDragEnd,
  };
}
